import React from 'react'
import defaults from '@config/defaults'
import { NextSeo } from 'next-seo'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Grid from '@material-ui/core/Grid'
import Divider from '@material-ui/core/Divider'
import Snackbar from '@material-ui/core/Snackbar'
import LinkIcon from '@material-ui/icons/Link'
import PropTypes from 'prop-types'
import Container from '@material-ui/core/Container'

const LOGO_VARIANTS = [
  {
    name: 'Blue logo for web',
    description: 'Use for websites or digital content with light backgrounds.',
    file: 'pga-of-america-logo-blue-on-transparent',
    preview: '/images/logos/pga-of-america-logo-blue-on-transparent.png',
    bgColor: '#fff',
    types: ['png'],
  },
  {
    name: 'Blue logo on white For web',
    description: 'Standard blue logo on solid white background for web use.',
    file: 'pga-of-america-logo-blue-on-white',
    preview: '/images/logos/pga-of-america-logo-blue-on-white.jpg',
    bgColor: '#fff',
    types: ['jpg'],
  },
  {
    name: 'For Print or Fax - One Color',
    description: 'For black & white printing, faxes, or single-color use.',
    file: 'pga-of-america-logo-black-on-transparent',
    preview: '/images/logos/pga-of-america-logo-black-on-transparent.png',
    bgColor: '#fff',
    types: ['png', 'jpg'],
  },
  {
    name: 'White logo for web',
    description: 'Use for websites or digital content with dark backgrounds.',
    file: 'pga-of-america-logo-white-on-transparent',
    preview: '/images/logos/pga-of-america-logo-white-on-transparent.png',
    bgColor: '#00205B',
    types: ['png'],
  },
]

const FILE_TYPE_LABELS = {
  png: 'PNG',
  jpg: 'JPG',
  eps: 'EPS',
}

const PGA_BLUE = '#00205B'

// Checkered background style for transparent logos
const TRANSPARENT_BG_STYLE = {
  backgroundImage: 'linear-gradient(45deg, #f0f0f0 25%, transparent 25%), linear-gradient(-45deg, #f0f0f0 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f0f0f0 75%), linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)',
  backgroundSize: '10px 10px',
  backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
  backgroundColor: 'white',
}

const getLogoUrl = (file, type) => `/images/logos/${file}.${type}`

const DownloadLogosSection = () => {
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '' })

  const handleCopyPermalink = (permalink) => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(permalink)
      setSnackbar({ open: true, message: 'Link copied!' })
    }
  }

  function getPermalink(file, type) {
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/images/logos/${file}.${type}`
    }
    return `/images/logos/${file}.${type}`
  }

  const previewBoxMinHeight = 250

  // Reusable LogoCard Component
  const LogoCard = ({
    name,
    description,
    previewSrc,
    altText,
    permalinkFile,
    permalinkType,
    buttonText,
    previewBoxStyle,
    gridColProps,
  }) => {
    const href = getLogoUrl(permalinkFile, permalinkType)
    const finalButtonText = buttonText
      || FILE_TYPE_LABELS[permalinkType.toUpperCase()]
      || permalinkType.toUpperCase()

    return (
      <Grid item {...gridColProps}>
        <Box
          border={1}
          borderColor="grey.200"
          borderRadius={8}
          p={3}
          display="flex"
          flexDirection="column"
          alignItems="center"
          height="100%"
          bgcolor="#fff"
        >
          <Box
            borderRadius={4}
            p={3}
            mb={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
            width="100%"
            style={{
              minHeight: previewBoxMinHeight,
              ...previewBoxStyle,
            }}
          >
            <a href={href} target="_blank" rel="noopener noreferrer">
              <img
                src={previewSrc}
                alt={altText || name}
                style={{
                  width: '100%',
                  height: '200px',
                  objectFit: 'contain',
                  background: 'transparent',
                  borderRadius: 4,
                }}
              />
            </a>
          </Box>
          <Typography variant="h6" align="center" gutterBottom style={{ fontWeight: 700 }}>
            {name}
          </Typography>
          <Typography variant="body2" align="center" color="textSecondary" gutterBottom>
            {description}
          </Typography>
          <Box display="flex" justifyContent="center" mt={2} flexWrap="wrap" gap={2}>
            <Box
              component="button"
              onClick={() => handleCopyPermalink(getPermalink(permalinkFile, permalinkType))}
              style={{
                background: 'transparent',
                border: `1px solid ${PGA_BLUE}`,
                borderRadius: 4,
                padding: (name.includes('Primary') || name.includes('White logo for web')) ? '8px 16px' : '6px 12px',
                color: PGA_BLUE,
                fontWeight: 600,
                cursor: 'pointer',
                margin: '0px 8px',
                fontSize: (name.includes('Primary') || name.includes('White logo for web')) ? '1rem' : undefined,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <LinkIcon style={{ marginRight: '8px' }} />
              {finalButtonText}
            </Box>
          </Box>
        </Box>
      </Grid>
    )
  }

  // PropTypes for LogoCard
  LogoCard.propTypes = {
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    previewSrc: PropTypes.string.isRequired,
    altText: PropTypes.string,
    permalinkFile: PropTypes.string.isRequired,
    permalinkType: PropTypes.string.isRequired,
    buttonText: PropTypes.string,
    previewBoxStyle: PropTypes.object.isRequired,
    gridColProps: PropTypes.object.isRequired,
  }

  // Data for logos
  const primaryWebLogo = LOGO_VARIANTS[0] // Blue logo for web
  const blueOnWhiteLogo = LOGO_VARIANTS[1] // Blue logo on white For web
  const blackPrintLogoDataBase = LOGO_VARIANTS[2] // For Print or Fax - One Color
  const whiteOnDarkLogo = LOGO_VARIANTS[3] // White logo for web

  const row1LogoData = [
    {
      name: primaryWebLogo.name,
      description: primaryWebLogo.description,
      previewSrc: primaryWebLogo.preview,
      altText: primaryWebLogo.name,
      permalinkFile: primaryWebLogo.file,
      permalinkType: primaryWebLogo.types[0], // png
      buttonText: 'Transparent PNG',
      previewBoxStyle: TRANSPARENT_BG_STYLE,
      gridColProps: { xs: 12, md: 6 },
    },
    {
      name: whiteOnDarkLogo.name,
      description: whiteOnDarkLogo.description,
      previewSrc: whiteOnDarkLogo.preview,
      altText: whiteOnDarkLogo.name,
      permalinkFile: whiteOnDarkLogo.file,
      permalinkType: whiteOnDarkLogo.types[0],
      buttonText: 'Transparent PNG',
      previewBoxStyle: {
        backgroundImage: "url('/images/navy-clubs-background.png')",
        backgroundRepeat: 'repeat',
        backgroundSize: 'auto',
        backgroundColor: '#1f2755',
      },
      gridColProps: { xs: 12, md: 6 },
    },
  ]

  const row2LogoData = [
    {
      name: blueOnWhiteLogo.name,
      description: blueOnWhiteLogo.description,
      previewSrc: blueOnWhiteLogo.preview,
      altText: blueOnWhiteLogo.name,
      permalinkFile: blueOnWhiteLogo.file,
      permalinkType: blueOnWhiteLogo.types[0], // jpg
      buttonText: FILE_TYPE_LABELS[blueOnWhiteLogo.types[0]],
      previewBoxStyle: { backgroundColor: blueOnWhiteLogo.bgColor },
      gridColProps: { xs: 12, sm: 6, md: 4 },
    },
    {
      // Black logo for print (Transparent PNG)
      name: 'Black logo for print',
      description: blackPrintLogoDataBase.description,
      previewSrc: blackPrintLogoDataBase.preview, // pga-of-america-logo-black-on-transparent.png
      altText: 'Black logo for print',
      permalinkFile: blackPrintLogoDataBase.file,
      permalinkType: 'png',
      buttonText: 'Transparent PNG',
      previewBoxStyle: TRANSPARENT_BG_STYLE,
      gridColProps: { xs: 12, sm: 6, md: 4 },
    },
    {
      // Black logo on white for print (JPG)
      name: 'Black logo on white for print',
      description: blackPrintLogoDataBase.description,
      previewSrc: '/images/logos/pga-of-america-logo-black-on-white.jpg', // Exact filename
      altText: 'Black logo on white for print',
      permalinkFile: 'pga-of-america-logo-black-on-white', // Exact filename base for permalink
      permalinkType: 'jpg',
      buttonText: 'JPG',
      previewBoxStyle: { backgroundColor: blackPrintLogoDataBase.bgColor },
      gridColProps: { xs: 12, sm: 6, md: 4 },
      customHref:
        '/images/logos/pga-of-america-logo-black-on-white.jpg',
    },
  ]

  return (
    <>
      <Typography variant="subtitle1" gutterBottom style={{ marginBottom: 16, textAlign: 'center' }}>
        See <a href="https://www.pgabrand.com/home/pga-of-america" target="_blank" rel="noopener noreferrer" style={{ color: PGA_BLUE, fontWeight: 600 }}>logo guide</a> for more information
      </Typography>

      {/* Row 1: Transparent PNGs - Titles derived from data or general titles */}
      <Grid container spacing={4} style={{ marginBottom: '40px' }} justifyContent="center">
        {row1LogoData.map(logo => (
          <LogoCard key={logo.name} {...logo} />
        ))}
      </Grid>

      {/* Row 2: Additional Formats */}
      <Typography variant="h5" align="center" gutterBottom style={{ marginBottom: '24px', marginTop: '40px' }}>
        Additional Formats
      </Typography>
      <Grid container spacing={4} justifyContent="center">
        {row2LogoData.map(logo => (
          <LogoCard key={logo.name} {...logo} />
        ))}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </>
  )
}

const LogosPage = () => (
  <>
    <NextSeo
      noindex={defaults.noindexNofollow}
      nofollow={defaults.noindexNofollow}
      title="PGA of America Logo & Brand"
      description="Download official PGA of America logos for web and print. Guidelines for usage and various logo formats available."
      canonical={`${defaults.baseUrl}logos`}
      openGraph={{
        title: 'PGA of America Logos',
        description: 'Download official PGA of America logos for web and print. Guidelines for usage and various logo formats available.',
        type: 'website',
        url: 'https://www.pga.com/logos',
        images: [{ url: '/images/logos/pga-of-america-logo-blue-on-transparent.png' }],
      }}
    />
    <Container maxWidth="lg">
      <Box p={{ xs: 2, md: 6 }}>
        <Typography variant="h3" align="center" gutterBottom style={{ fontWeight: 700, letterSpacing: 1 }}>
          PGA of America Logo
        </Typography>
        <DownloadLogosSection />
        <Divider style={{ margin: '32px 0' }} />
        {/* <LogoSection />
        <ColorSection />
        <MinimumSizeSection />
        <EmbroiderySection />
        <UsageExamplesSection /> */}
      </Box>
    </Container>
  </>
)

LogosPage.getInitialProps = async ({ query }) => ({ query })

export default LogosPage
